import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ModalComponent, ModalData, ModalTitleIcons } from '@modeso/aumico-lib-core-fe';
import { AccountType, AnnualReportBusinessModel, IAccount, ItemType } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, first } from 'rxjs/operators';
import { localizationKeys } from '../../constants/localizationKeys';
import { MenuAction } from '../../enums/menu-action.enum';
import { FliInEditModeService } from '../../services/fli-in-edit-mode.service';

@UntilDestroy()
@Component({
    selector: 'aumico-editable-account-form',
    templateUrl: './editable-account-form.component.html',
    styleUrls: ['./editable-account-form.component.scss'],
})
export class EditableAccountFormComponent implements OnInit {
    private _isEditing = false;
    addNewAccountMode = false;
    get isEditing(): boolean {
        return this._isEditing;
    }
    set isEditing(value: boolean) {
        this._isEditing = value;
        if (this.account) {
            try {
                this.account["isEditing"] = value;
            } catch (e) { }
        }
    }
    @Input() flisLevelsIndentations: number[] = [];
    @Input() account: IAccount;
    @Input() hasPreviousData: boolean;
    @Input() parentType: ItemType;
    @Input() parentName: string;
    @Input() businessModel: AnnualReportBusinessModel;
    @Input() disableEditingPreviousYearValue = false;
    @Input() supportOperation = false;
    @Input() errorInPreviousAnnualProfit = false;
    @Input() isAutomaticTransferOfAnnualResultEnabled: boolean | undefined = undefined;

    @Output() saveAccount = new EventEmitter<IAccount>();
    @Output() removeAccount = new EventEmitter<MouseEvent>();
    @Output() cancelAccountCreation = new EventEmitter<MouseEvent>();
    @Output() scrollToAccount = new EventEmitter<void>();
    @Output() onMenuActionClicked = new EventEmitter<string>();
    AccountType = AccountType;

    public accountForm: FormGroup;
    public localizationKeys = localizationKeys;
    noAccountNumber: boolean;
    public isPriorYearUnlockedForThisRow = false;

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly matIconRegistry: MatIconRegistry,
        private readonly domSanitizer: DomSanitizer,
        public readonly dialog: MatDialog,
        private readonly fliEditModeService: FliInEditModeService,
    ) {
        this.matIconRegistry.addSvgIcon(
            'inline-save',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/aumico-pattern-grey-save.svg'),
        );
        this.matIconRegistry.addSvgIcon(
            'close',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/aumico-pattern-grey-close-circle.svg'),
        );
    }

    ngOnInit(): void {
        this.fliEditModeService.fliInEditMode$
            .pipe(
                filter((fliName) => fliName != null),
                untilDestroyed(this),
            )
            .subscribe((fliName) => {
                if (fliName === this.parentName) {
                    setTimeout(() => this.onCancelEditAccount());
                }
            });
        // Check if this is Empty Object so it'll be in add mode
        this.isEditing = Object.keys(this.account).length === 0;

        if (this.isEditing) {
            this.addNewAccountMode = true;

            this.scrollToAccount.emit();
        }
        if (
            this.account &&
            (this.account.type === AccountType.SPECIAL_ACCOUNT_DELETE_ON_MAPPING ||
                this.account.type === AccountType.UNCOVERED_SHORTFALL)
        ) {
            this.noAccountNumber = true;
        }

        if (this.noAccountNumber) {
            this.accountForm = this.formBuilder.group({
                accountNumber: ['', []],
                name: ['', Validators.required],
                calculatedOpeningBalance: [],
                calculatedClosingBalance: [],
            });
        } else {
            this.accountForm = this.formBuilder.group({
                accountNumber: ['', [Validators.required]],
                name: ['', Validators.required],
                calculatedOpeningBalance: [],
                calculatedClosingBalance: [],
            });
        }

        if (this.account.accountNumber !== undefined) {
            this.accountForm.patchValue(this.account);
        }
    }

    public onSaveAccount(): void {
        if (this.accountForm.invalid) {
            this.openInvalidFormWarningDialog();
            return;
        }
        const accountFormValue: Partial<IAccount> = this.accountForm.value as Partial<IAccount>;
        const editedAccount: IAccount = {
            ...this.account,
            ...accountFormValue,
            calculatedOpeningBalance: accountFormValue.calculatedOpeningBalance ?? 0,
            calculatedClosingBalance: accountFormValue.calculatedClosingBalance ?? 0,
            closingBalance: accountFormValue.calculatedClosingBalance ?? 0,
            openingBalance:
                this.hasPreviousData && accountFormValue.calculatedOpeningBalance
                    ? accountFormValue.calculatedOpeningBalance
                    : 0,
        };

        this.saveAccount.emit(editedAccount);
        this.isEditing = false;
        this.addNewAccountMode = false;
        this.isPriorYearUnlockedForThisRow = false;
    }

    public onUnlockPriorYear(): void {
        this.isPriorYearUnlockedForThisRow = true;
    }

    private openInvalidFormWarningDialog(): void {
        this.dialog
            .open<ModalComponent, ModalData>(ModalComponent, {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: {
                    showCloseButton: true,
                    titleIcon: ModalTitleIcons.WARNING,
                    titleLocalizationKey: localizationKeys.aumico_editable_account_invalid_data_modal_title,
                    content: {
                        text: [localizationKeys.aumico_editable_account_invalid_data_modal_content],
                    },
                },
            })
            .afterClosed()
            .pipe(first())
            .subscribe();
    }

    public onCancelEditAccount(): void {
        if (this.account.accountNumber === undefined) {
            this.cancelAccountCreation.emit();
        } else {
            this.isEditing = false;
            this.addNewAccountMode = false;
            this.isPriorYearUnlockedForThisRow = false;
        }
    }

    removeAccountFrom(event: MouseEvent): void {
        this.removeAccount.emit(event);
    }

    splitAccountClicked(): void {
        this.onMenuActionClicked.emit(MenuAction.SWITCH_ACCOUNT);
    }

    onCancelAccountCreation(): void {
        this.cancelAccountCreation.emit();
        this.isEditing = false;
        this.addNewAccountMode = false;
        this.isPriorYearUnlockedForThisRow = false;
    }

    onMenuActionClickedHandler($event: MenuAction): void {
        if ($event === MenuAction.EDIT) {
            this.isEditing = true;
        } else {
            this.onMenuActionClicked.emit($event);
        }
    }
}
